// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// The pieces the framework doc pages are built from, so those pages read as
// content rather than as a wall of class names, and so they stay consistent
// with each other without every page restating the same styles.
// ==========================================================================

import React from 'react';

export const DocH2: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  <h2
    id={id}
    className="font-display text-[24px] font-extrabold text-content-primary mb-[20px] tracking-[-0.4px]"
  >
    {children}
  </h2>
);

export const DocH3: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  // scroll-mt keeps the heading clear of the sticky header when the sidebar
  // jumps to an anchor; without it the target lands underneath it.
  <h3
    id={id}
    className="font-display text-[18px] font-bold text-content-primary mt-[34px] mb-[12px] scroll-mt-[90px]"
  >
    {children}
  </h3>
);

export const DocP: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[14px] leading-[1.75] text-content-secondary mb-[16px]">
    {children}
  </p>
);

export const DocList: React.FC<{
  items: React.ReactNode[];
  ordered?: boolean;
}> = ({ items, ordered }) => (
  <ol className="flex flex-col gap-[9px] mb-[18px] list-none p-0">
    {items.map((item, index) => (
      <li
        key={index}
        className="text-[14px] leading-[1.7] text-content-secondary flex gap-[10px]"
      >
        {ordered ? (
          <span className="shrink-0 w-[20px] h-[20px] mt-[1px] rounded-full bg-surface-section border border-border-dark/[0.14] font-mono text-[11px] flex items-center justify-center">
            {index + 1}
          </span>
        ) : (
          <span className="mt-[9px] w-[5px] h-[5px] rounded-full bg-blue shrink-0" />
        )}
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

export const DocCode: React.FC<{ children: string }> = ({ children }) => (
  <pre className="mb-[18px] p-[14px] rounded-[10px] bg-surface-section border border-border-dark/[0.11] overflow-x-auto font-mono text-[12.5px] leading-[1.6] text-content-secondary">
    {children}
  </pre>
);

export const DocTable: React.FC<{
  head: string[];
  rows: React.ReactNode[][];
}> = ({ head, rows }) => (
  // Its own scroller: a wide table must not make the whole page scroll
  // sideways on a narrow window.
  <div className="mb-[18px] overflow-x-auto">
    <table className="w-full border-collapse text-[13.5px]">
      <thead>
        <tr>
          {head.map((cell) => (
            <th
              key={cell}
              className="text-left font-display font-bold text-content-primary border-b border-border-dark/[0.16] py-[8px] pr-[14px]"
            >
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className="align-top text-content-secondary border-b border-border-dark/[0.08] py-[8px] pr-[14px] leading-[1.65]"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
